<template>
    <div class="side-bar" :class="{ mini: !expanded }">
        <div class="head-logo" :class="{ mini: !expanded }">
            <a v-if="expanded && logo" @click="goHome" class="cursor-pointer w-100">
                <img class="logo" :src="logo">
            </a>
            <a v-else-if="expanded && !logo && theme === 'light'" @click="goHome" class="cursor-pointer w-100">
                <img class="logo" src="/svg/eljap-Interactivo.svg">
            </a>
            <a v-else-if="expanded && !logo && theme === 'dark'" @click="goHome" class="cursor-pointer w-100">
                <img class="logo" src="/svg/eljap-Interactivo_cb.svg">
            </a>
            <div class="min-side-bar-container" :class="{ mini: !expanded }">
                <button type="button"
                    class="min-side-bar d-flex justify-content-center align-items-center cursor-pointer"
                    :title="$t('Main menu')" @click="clickMenu">
                    <i class="fas fa-bars"></i>
                </button>
            </div>
        </div>
        <div class="side-bar-body" :class="{ mini: !expanded }">
            <a class="side-bar-option cursor-pointer" :title="$t('Search')" @click="openSpotlight">
                <div class="side-bar-option-icon"><i class="fa-solid fa-magnifying-glass"></i></div>
                <div class="side-bar-option-text" v-if="expanded">{{ $t("Search") }}</div>
            </a>

            <a v-if="loggedIn && !isAnonymousUser" class="side-bar-option cursor-pointer" :title="$t('Create room')" @click="showCreateRoom">
                <div class="side-bar-option-icon"><i class="fa-solid fa-plus"></i></div>
                <div class="side-bar-option-text" v-if="expanded">{{ $t("Create room") }}</div>
            </a>

            <a class="side-bar-option cursor-pointer" :class="{ selected: page === 'showall' }" :title="$t('Show All')"
                @click="showAllRooms">
                <div class="side-bar-option-icon"><i class="fa-solid fa-list-ul"></i></div>
                <div class="side-bar-option-text" v-if="expanded">{{ $t("Show All") }}</div>
            </a>

            <a v-if="loggedIn" class="side-bar-option cursor-pointer" :class="{ selected: page === 'myrooms' }"
                :title="$t('My rooms')" @click="showMyRooms">
                <div class="side-bar-option-icon"><i class="fa-solid fa-house"></i></div>
                <div class="side-bar-option-text" v-if="expanded">{{ $t("My rooms") }}</div>
            </a>

            <a v-if="loggedIn" class="side-bar-option cursor-pointer" :class="{ selected: page === 'favorite' }"
                :title="$t('Favorite')" @click="showFavourites">
                <div class="side-bar-option-icon"><i class="fa-solid fa-star"></i></div>
                <div class="side-bar-option-text" v-if="expanded">{{ $t("Favorite") }}</div>
            </a>

            <a v-if="loggedIn" class="side-bar-option cursor-pointer" :class="{ selected: page === 'activity' }"
                :title="$t('Activity')" @click="showActivity">
                <div class="side-bar-option-icon">
                    <i class="fa-solid fa-bell position-relative">
                        <div v-if="notificationBubble" class="notification-bubble"></div>
                    </i>
                </div>
                <div class="side-bar-option-text" v-if="expanded">{{ $t("Activity") }}</div>
            </a>
        </div>

        <footer class="sidebar-footer">
            <button type="button" @click="login" class="sidebar-login-btn cursor-pointer"
                v-if="!loggedIn && !isAnonymousUser">
                <i class="fas fa-sign-in"></i>
                <span v-if="expanded">{{ $t("Login") }}</span>
            </button>

            <div v-else class="dropdown sidebar-user-dropdown">
                <button class="dropdown-toggle sidebar-user-btn cursor-pointer" type="button"
                    data-bs-toggle="dropdown" aria-expanded="false">
                    <i class="fa-regular fa-circle-user"></i>
                    <span v-if="expanded" class="sidebar-user-name"
                        :title="$t('$USER').replace('$USER', renderName(profileName))">
                        {{ $t("$USER").replace("$USER", renderName(profileName)) }}
                    </span>
                </button>
                <ul class="dropdown-menu sidebar-user-menu">
                    <li v-if="loggedIn && !isAnonymousUser">
                        <a :title="$t('Settings')" @click="showSettings" class="cursor-pointer dropdown-item">
                            <i class="fa-regular fa-circle-user"></i>
                            <span>{{ renderName(profileName) }}</span>
                        </a>
                    </li>
                    <li v-if="loggedIn && !isAnonymousUser"><hr class="dropdown-divider"></li>
                    <li v-if="loggedIn && !isAnonymousUser">
                        <a :title="$t('Settings')" @click="showSettings" class="cursor-pointer dropdown-item">
                            <i class="fa-solid fa-gear"></i>{{ $t("Settings") }}
                        </a>
                    </li>
                    <li v-if="loggedIn && !isAnonymousUser">
                        <a :title="$t('Organizations')" @click="showOrganizations" class="cursor-pointer dropdown-item">
                            <i class="fa-solid fa-building"></i>{{ $t("Organizations") }}
                        </a>
                    </li>
                    <li v-if="loggedIn && !isAnonymousUser"><hr class="dropdown-divider"></li>
                    <li>
                        <a :title="$t('Theme')" @click="invertTheme" class="cursor-pointer dropdown-item">
                            <i class="fa-solid" :class="theme === 'dark' ? 'fa-moon' : 'fa-sun'"></i>{{ $t("Theme") }}
                        </a>
                    </li>
                    <li>
                        <a :title="$t('Language')" @click="selectLanguage" class="cursor-pointer dropdown-item">
                            <i class="fas fa-language"></i>{{ $t("Language") }}
                        </a>
                    </li>
                    <li>
                        <a :title="$t('Logout')" @click="logout" class="cursor-pointer dropdown-item">
                            <i class="fa-solid fa-arrow-right-from-bracket"></i>{{ $t("Logout") }}
                        </a>
                    </li>
                </ul>
            </div>

            <div class="sidebar-legal-links" :class="{ mini: !expanded }">
                <router-link class="sidebar-legal-link" :to="{ name: 'terms' }" :title="$t('Terms of use')">
                    <i v-if="!expanded" class="fa-regular fa-file-lines"></i>
                    <span v-else>{{ $t("Terms of use") }}</span>
                </router-link>
                <span v-if="expanded" class="separator">|</span>
                <router-link class="sidebar-legal-link" :to="{ name: 'privacy' }" :title="$t('Privacy policy')">
                    <i v-if="!expanded" class="fa-solid fa-shield-halved"></i>
                    <span v-else>{{ $t("Privacy policy") }}</span>
                </router-link>
            </div>
        </footer>
    </div>

    <SpotlightSearchModal v-if="spotlightOpen" @close="spotlightOpen = false" />
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { useVModel } from "@/utils/v-model";
import { ExitPreventer } from "@/utils/exit-preventer";
import SpotlightSearchModal from "@/components/modals/SpotlightSearchModal.vue";

import { ColorThemeName, AppPreferences } from "@/control/app-preferences";
import { AuthController } from "@/control/auth";
import { RoomController } from '@/control/room';
import { Request } from "@asanrom/request-browser";
import { getUniqueStringId } from "@/utils/unique-id";
import { ApiNotifications } from "@/api/api-group-notifications";
import { Timeouts } from "@/utils/timeout";

export default defineComponent({
    name: "MenuSideBar",
    components: { SpotlightSearchModal },
    emits: ["update:expanded", "toggle-menu", "openModal"],
    props: {
        expanded: Boolean,
    },
    setup(props) {
        return {
            expandedStatus: useVModel(props, "expanded"),
            displayStatus: useVModel(props, "display"),
            loadRequestId: getUniqueStringId(),
        };
    },
    data: function () {
        return {
            page: "",
            platformName: import.meta.env.VITE__PLATFORM_NAME || "Platform",
            loggedIn: AuthController.isAuthenticated(),
            profileImage: AuthController.ProfileImage,
            theme: AppPreferences.Theme,
            profileName: AuthController.ProfileName || AuthController.Username || AuthController.anonName || "",
            isAnonymousUser: AuthController.isAnon(),

            roomId: "",
            logo: "",
            isBackground: false,

            notificationBubble: false,
            spotlightOpen: false,
        };
    },
    methods: {
        openSpotlight: function () {
            this.spotlightOpen = true;
        },

        showCreateRoom: function () {
            this.$emit("openModal", "create-room");
        },

        logout: function () {
            this.$emit("openModal", "logout");
            this.onAuthChanged();
        },

        close: function () {
            this.displayStatus = false;
        },

        updatePage: function () {
            this.page = this.$route ? (this.$route.name as string) : "";
        },

        clickMenu: function () {
            this.$emit("toggle-menu");
        },

        openUserSettings: function () {
            this.$emit("openModal", "account-settings");
        },

        selectLanguage: function () {
            this.$emit("openModal", "change-language-modal");
        },

        invertTheme: function () {
            AppPreferences.SetTheme(this.theme === "dark" ? "light" : "dark");
        },

        login: function () {
            this.$requireLogin();
        },

        onAuthChanged: function () {
            this.loggedIn = AuthController.isAuthenticated();
            this.profileName = AuthController.ProfileName || AuthController.Username || AuthController.anonName || "";
            this.isAnonymousUser = AuthController.isAnon();
            this.profileImage = AuthController.ProfileImage;
            if(this.loggedIn) {
                this.loadUnreadNotifications();
            } else {
                this.notificationBubble = false;
            }
        },

        onThemeChanged: function (t: ColorThemeName) {
            this.theme = t;
        },

        renderName: function (name: string): string {
            const cleanName = ((name + "").split(" ")[0] + "").split(",")[0] || this.$t("Guest");

            if (cleanName.length <= 15) {
                return cleanName;
            }

            return cleanName.slice(0, 15) + "...";
        },

        onRoomMetaUpdated: function (roomId, meta) {
            this.roomId = roomId;
            if (meta) {
                this.logo = meta.logo;
                this.isBackground = meta.background !== "";
            } else {
                this.logo = "";
                this.isBackground = false;
            }
        },

        showSettings: function () {
            ExitPreventer.handlePrevent("exit-room", () => {
                RoomController.leaveRoom();
                this.$router.push({ name: "settings", query: { tab: "profile" } });
            });
        },

        showOrganizations: function () {
            ExitPreventer.handlePrevent("exit-room", () => {
                RoomController.leaveRoom();
                this.$router.push({ name: "organization" });
            });
        },

        showAllRooms: function () {
            ExitPreventer.handlePrevent("exit-room", () => {
                RoomController.leaveRoom();
                this.$router.push({ name: "showall" });
            });
        },

        showMyRooms: function () {
            ExitPreventer.handlePrevent("exit-room", () => {
                RoomController.leaveRoom();
                this.$router.push({ name: "myrooms" });
            });
        },

        showFavourites: function () {
            ExitPreventer.handlePrevent("exit-room", () => {
                RoomController.leaveRoom();
                this.$router.push({ name: "favorite" });
            });
        },

        showActivity: function () {
            ExitPreventer.handlePrevent("exit-room", () => {
                RoomController.leaveRoom();
                this.$router.push({ name: "activity" });
            });
        },

        goHome: function () {
            ExitPreventer.handlePrevent("exit-room", () => {
                RoomController.leaveRoom();
                this.$router.push({ name: "home" });
            });
        },
        loadUnreadNotifications: function () {
            Timeouts.Abort(this.loadRequestId);
            Request.Abort(this.loadRequestId);
            Request.Pending(this.loadRequestId, ApiNotifications.GetUnreadNotifications())
                .onSuccess((response) => {
                    this.notificationBubble = response.unread;
                })
                .onRequestError((err, handleErr) => {
                    handleErr(err, {
                        unauthorized: () => {
                            this.$requireLogin();
                        },
                        temporalError: () => {
                            Timeouts.Set(this.loadRequestId, 1500, this.loadUnreadNotifications.bind(this));
                        },
                    });
                })
                .onUnexpectedError((err) => {
                    console.error(err);
                    Timeouts.Set(this.loadRequestId, 1500, this.loadUnreadNotifications.bind(this));
                });
        },
        onNotification: function () {
            this.notificationBubble = true;
        },
    },
    mounted: function () {
        this.updatePage();
        this.$options.roomMetaHandler = this.onRoomMetaUpdated.bind(this);
        RoomController.on("room-meta", this.$options.roomMetaHandler);
        this.onRoomMetaUpdated(RoomController.room, RoomController.meta);

        this.$listenOnAppEvent("auth-status-changed", this.onAuthChanged.bind(this));
        this.$listenOnAppEvent("theme-changed", this.onThemeChanged.bind(this));
        this.$listenOnAppEvent("notification", this.onNotification.bind(this));
        this.$listenOnAppEvent("notification-read", this.loadUnreadNotifications.bind(this));
    },
    beforeUnmount: function () {
        RoomController.removeEventListener("room-meta", this.$options.roomMetaHandler);
    },
    watch: {
        $route: function () {
            this.updatePage();
        },
    },
});
</script>

<style scoped>
@import "@/style/layout/side-bar.css";
</style>
